import FormControl from "common/components/FormControl/FormControl";
import RepeatableFieldArray from "common/components/FormControl/RepeatableFieldArray";
import { useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import CrudForm from "common/components/Crud/CrudForm";
import Button from "common/components/Button/Button";
import type { Event } from "types";
import EventTypeSelect from "components/select/EventTypeSelect/EventTypeSelect";
import useItem from "hooks/useItem";
import AlertsContext from "providers/AlertProvider";

type Props = {
  onSubmit: (values: any) => any;
  onCancel?: () => void;
  onDelete?: () => void;
  item?: Partial<Event>;
};

function EventForm({ onSubmit, onCancel, onDelete, item }: Props) {
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);
  const { removeItem, isFetching: isDeleting } = useItem<Event>("events", null);

  const handleDelete = async () => {
    if (!item?.id) return;
    try {
      await removeItem(item.id);
      setAlert(t("common.deleted"), "success");
      onDelete?.();
    } catch {
      setAlert(t("common.error"), "danger");
    }
  };

  const initialValues = useMemo(
    () =>
      item
        ? {
            ...item,
            eventType: item.eventType?.id,
          }
        : {},
    [item],
  );

  return (
    <CrudForm
      onDelete={item?.id ? handleDelete : undefined}
      onSubmit={onSubmit}
      onCancel={onCancel}
      initialValues={initialValues}
      headerIcon="CalendarDaysIcon"
      i18nNamespace="events"
      render={() => (
        <>
          <div className="white-box flex flex-col gap-2">
            <FormControl name="name" title={t("events.name")} required />
            <FormControl
              name="startDateTime"
              type="date"
              title={t("events.startDateTime")}
              required
            />
            <FormControl
              name="endDateTime"
              type="date"
              title={t("events.endDateTime")}
              required
            />
            <FormControl name="location" title={t("events.location")} />
            <FormControl
              name="description"
              type="richtext"
              title={t("events.description")}
            />
            <FormControl
              name="eventType"
              type="custom"
              isSelect
              title={t("events.eventType")}
            >
              <EventTypeSelect />
            </FormControl>
            <FormControl
              name="medias"
              type="file"
              title={t("events.medias")}
              acceptImages
              acceptPdf
            />
            <RepeatableFieldArray
              name="links"
              title={t("events.links")}
              addLabel={t("events.addLink")}
              emptyItem={{ name: "", url: "" }}
              renderItem={(fieldName) => (
                <div className="flex flex-col gap-2">
                  <FormControl
                    name={`${fieldName}.name`}
                    title={t("events.linkName")}
                  />
                  <FormControl
                    name={`${fieldName}.url`}
                    title={t("events.linkUrl")}
                  />
                </div>
              )}
            />
          </div>
          {item?.id && (
            <div className="white-box mt-4 flex justify-end">
              <Button
                title={t("common.delete")}
                type="error"
                icon="TrashIcon"
                confirm
                onClick={handleDelete}
                loading={isDeleting}
              />
            </div>
          )}
        </>
      )}
    />
  );
}

export default EventForm;
